import { IconArrow } from '@assets/icon-arrow';
import { IconPersonPlaceholder } from '@assets/icon-person-placeholder';
import { Avatar } from '@components/atoms/Avatar/Avatar';
import { Link } from '@components/atoms/Link/Link';
import type { Person as PersonType } from '@lib/graphql/generated';
import type { PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import React from 'react';
import { twMerge } from 'tailwind-merge';

export interface PersonProps extends PropsWithClassProps {
  person: PersonType;
  type?: 'compact' | 'medium';
}

export const Person = React.forwardRef<HTMLDivElement, PersonProps>(({ person, type, classProps }, ref) => {
  return type === 'compact' ? (
    <Link
      href={`/personen/${person.slug || person.id}`}
      classProps={{
        root: twMerge(
          'grid group min-w-0 max-w-full cursor-pointer grid-cols-[auto_1fr] items-center gap-4 rounded-xl streaming-entry-bg border-0.5 border-primary/0 transition-colors duration-150 hover:border-primary p-4',
          classProps?.root,
        ),
      }}
    >
      <div className="z-10 size-12 overflow-hidden rounded-full">
        {person.image?.url ? (
          <Avatar
            avatar={person.image}
            alt={`Foto von ${person.name}`}
            classProps={{
              root: 'w-full h-full pointer:group-hover:scale-105 z-0 pointer:transition-transform pointer:duration-300',
            }}
          />
        ) : (
          <div className="size-full bg-gray-600 text-gray-500">
            <IconPersonPlaceholder classProps={{ root: 'size-full' }} />
          </div>
        )}
      </div>
      <div className="grid w-full min-w-0 max-w-full grid-cols-[auto_1fr] justify-start gap-2 text-foreground">
        <div className="line-clamp-1 max-w-full break-all font-bold text-base">{person.name}</div>
        <div className="flex">
          <div className="w-0 transition-all duration-200 group-hover:w-full"></div>
          <IconArrow
            classProps={{
              root: 'inline h-2 my-auto flex-none transition-all duration-300 group-hover:text-primary',
            }}
          />
        </div>
      </div>
    </Link>
  ) : (
    <div
      ref={ref}
      className={twMerge(
        'group grid min-h-60 w-52 items-center justify-center rounded-xl border-0.5 border-primary/0 bg-gradient-to-t from-gray-820 to-gray-700 px-10 py-6 text-center transition-colors duration-150 hover:border-primary',
        classProps?.root,
      )}
    >
      <Link href={`/personen/${person.slug || person.id}`}>
        <div className="z-10 mx-auto inline-block size-32 overflow-hidden rounded-full">
          {person.image?.url ? (
            <Avatar
              avatar={person.image}
              alt={`Foto von ${person.name}`}
              classProps={{
                root: 'w-full h-full pointer:group-hover:scale-105 z-0 pointer:transition-transform pointer:duration-300',
              }}
            />
          ) : (
            <div className="size-full bg-gray-600 text-gray-500">
              <IconPersonPlaceholder classProps={{ root: 'size-full' }} />
            </div>
          )}
        </div>
        <div className="font-herokid text-foreground text-xl">{person.name}</div>
        {(person.meta?.rolenames.length > 0 || person.meta?.positions.length > 0) && (
          <div className="mt-3 font-bold text-2xs text-foreground/40 uppercase leading-snug tracking-2px">
            {person.meta.rolenames.join(', ') || person.meta.positions?.join(', ')}
          </div>
        )}
      </Link>
    </div>
  );
});

Person.displayName = 'Person';
